import {
  HttpRequest,
  HttpResponse,
  type HttpEvent,
  type HttpHandler,
  type HttpInterceptor,
} from '@angular/common/http';
import {
  type Observable,
  catchError,
  last,
  map,
  shareReplay,
  tap,
  throwError,
} from 'rxjs';
import { decodeJwt, onHttpCacheIdentityScopeChanging } from '@ups/xplat/utils';

/** One cacheable lookup: which GETs it covers, how long they live, and which writes drop them. */
export interface HttpLookupCacheRule {
  /** Matched against the request's full URL (with query string). */
  readonly pattern: RegExp;
  /** How long a response is served from memory, in ms. */
  readonly ttlMs: number;
  /**
   * Successful non-GET requests whose URL matches drop every entry this rule cached,
   * so an in-app admin edit shows on the next open instead of after the TTL.
   */
  readonly invalidatedBy?: RegExp;
}

interface LookupCacheEntry {
  readonly rule: HttpLookupCacheRule;
  readonly expiresAt: number;
  readonly response$: Observable<HttpResponse<unknown>>;
}

/** Above this many entries the oldest is evicted (typed searches would otherwise grow without bound). */
export const HTTP_LOOKUP_CACHE_MAX_ENTRIES = 500;

/**
 * In-memory GET cache for reference-data lookups (WI 35278), registered per app through
 * `MY_HTTP_CLIENT_INTERCEPTORS` with that app's own allowlist of rules.
 *
 * - Caches only requests that carry a bearer token, and keys on that token's user (`sub`) + the
 *   full URL + the Accept header (camel and Pascal clients get different payloads). The user comes
 *   from the request itself, so the page's first lookups are cached before the profile loads, and
 *   one user's scoped list is never served to another. Everything is dropped on sign-out or a user
 *   switch (`onHttpCacheIdentityScopeChanging`).
 * - Concurrent identical GETs share one request; errors are never cached.
 * - Every subscriber gets its own deep copy of the body, so a caller that mutates a list
 *   (sorts it, pushes a blank option) cannot change what the next caller sees.
 */
export class HttpLookupCacheInterceptor implements HttpInterceptor {
  private readonly entries = new Map<string, LookupCacheEntry>();

  constructor(
    private readonly rules: readonly HttpLookupCacheRule[],
    private readonly now: () => number = Date.now
  ) {
    // Only a user leaving (sign-out or switch) empties the cache. The first fill from no scope
    // fires on every cold load once the profile resolves, after the first lookups were already
    // cached under that same token's user, so clearing then would refetch them all.
    onHttpCacheIdentityScopeChanging((from) => {
      if (from) {
        this.clear();
      }
    });
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (req.method !== 'GET') {
      return this.invalidateOnSuccess(req, next);
    }
    const rule = this.rules.find((r) => r.pattern.test(req.urlWithParams));
    const user = rule && !req.reportProgress ? bearerUser(req) : null;
    if (!rule || !user) {
      return next.handle(req);
    }

    const key = `${user}|${req.urlWithParams}|${req.headers.get('Accept') ?? ''}|${req.responseType}`;
    let entry = this.entries.get(key);
    if (!entry || entry.expiresAt <= this.now()) {
      entry = this.store(key, rule, req, next);
    }
    return entry.response$.pipe(
      map((response) => response.clone({ body: cloneBody(response.body) }))
    );
  }

  /** Drop every cached response. */
  clear(): void {
    this.entries.clear();
  }

  private store(
    key: string,
    rule: HttpLookupCacheRule,
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): LookupCacheEntry {
    const response$ = next.handle(req).pipe(
      last(
        (event): event is HttpResponse<unknown> => event instanceof HttpResponse
      ),
      catchError((error: unknown) => {
        if (this.entries.get(key)?.response$ === response$) {
          this.entries.delete(key);
        }
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    const entry: LookupCacheEntry = {
      rule,
      expiresAt: this.now() + rule.ttlMs,
      response$,
    };
    this.entries.delete(key);
    this.entries.set(key, entry);
    if (this.entries.size > HTTP_LOOKUP_CACHE_MAX_ENTRIES) {
      const oldest = this.entries.keys().next().value;
      if (oldest !== undefined) {
        this.entries.delete(oldest);
      }
    }
    return entry;
  }

  private invalidateOnSuccess(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const affected = this.rules.filter((r) =>
      r.invalidatedBy?.test(req.urlWithParams)
    );
    if (!affected.length) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          for (const [key, entry] of this.entries) {
            if (affected.includes(entry.rule)) {
              this.entries.delete(key);
            }
          }
        }
      })
    );
  }
}

/** The `sub` of the request's bearer token, or null when it has none (never cached). */
function bearerUser(req: HttpRequest<unknown>): string | null {
  const token = /^Bearer\s+(\S+)$/i.exec(
    req.headers.get('Authorization') ?? ''
  )?.[1];
  if (!token) {
    return null;
  }
  try {
    const sub: unknown = decodeJwt(token)?.sub;
    return typeof sub === 'string' && sub.trim() ? sub : null;
  } catch {
    return null;
  }
}

function cloneBody<T>(body: T): T {
  if (body === null || typeof body !== 'object') {
    return body;
  }
  return typeof structuredClone === 'function'
    ? structuredClone(body)
    : (JSON.parse(JSON.stringify(body)) as T);
}
